import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { getLoggedinUser } from '../../helpers/api_helper';

export interface Notification {
  id: string;
  type: string;
  data: {
    title: string;
    content?: string;
    message?: string;
    [key: string]: any;
  };
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  read: number;
  today: number;
  this_week: number;
}

export interface NotificationResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  unread: number;
}

// Hook for fetching and managing notifications
export const useNotifications = (userId?: string | number, autoRefresh: boolean = false) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [unreadCount, setUnreadCount] = useState(0);

  // Get the current user if userId is not provided
  const currentUser = getLoggedinUser();
  const effectiveUserId = userId || currentUser?.user?.id;

  // Fetch notifications
  const fetchNotifications = useCallback(async (page: number = 1, limit: number = 10) => {
    if (!effectiveUserId) {
      setError('User ID is required');
      return;
    }

    if (!currentUser?.token) {
      setError('Authentication token is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${config.api.API_URL}/notifications/user/${effectiveUserId}?page=${page}&limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${currentUser?.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setNotifications(data.data.notifications);
        setPagination(data.data.pagination);
        setUnreadCount(data.data.unread);
      } else {
        throw new Error(data.message || 'Failed to fetch notifications');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch notifications';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, currentUser?.token]);

  // Fetch notification statistics
  const fetchStats = useCallback(async () => {
    if (!effectiveUserId) return;

    try {
      const response = await fetch(
        `${config.api.API_URL}/notifications/user/${effectiveUserId}/stats`,
        {
          headers: {
            'Authorization': `Bearer ${currentUser?.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setStats(data.data.overview);
      }
    } catch (err) {
      console.error('Failed to fetch notification stats:', err);
    }
  }, [effectiveUserId, currentUser?.token]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!effectiveUserId) return false;

    try {
      const response = await fetch(
        `${config.api.API_URL}/notifications/${notificationId}/read`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${currentUser?.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId: effectiveUserId })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, is_read: true }
              : notification
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        return true;
      } else {
        throw new Error(data.message || 'Failed to mark notification as read');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark notification as read';
      toast.error(errorMessage);
      return false;
    }
  }, [effectiveUserId, currentUser?.token]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!effectiveUserId) return false;

    try {
      const response = await fetch(
        `${config.api.API_URL}/notifications/user/${effectiveUserId}/read-all`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${currentUser?.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, is_read: true }))
        );
        setUnreadCount(0);
        toast.success('All notifications marked as read');
        return true;
      } else {
        throw new Error(data.message || 'Failed to mark all notifications as read');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark all notifications as read';
      toast.error(errorMessage);
      return false;
    }
  }, [effectiveUserId, currentUser?.token]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!effectiveUserId) return false;

    try {
      const response = await fetch(
        `${config.api.API_URL}/notifications/${notificationId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${currentUser?.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId: effectiveUserId })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setNotifications(prev => 
          prev.filter(notification => notification.id !== notificationId)
        );
        // Update unread count if the deleted notification was unread
        const deletedNotification = notifications.find(n => n.id === notificationId);
        if (deletedNotification && !deletedNotification.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        toast.success('Notification deleted');
        return true;
      } else {
        throw new Error(data.message || 'Failed to delete notification');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete notification';
      toast.error(errorMessage);
      return false;
    }
  }, [effectiveUserId, currentUser?.token, notifications]);

  // Refresh notifications
  const refresh = useCallback(() => {
    fetchNotifications(pagination.page, pagination.limit);
  }, [fetchNotifications, pagination.page, pagination.limit]);

  // Load more notifications (pagination)
  const loadMore = useCallback(() => {
    if (pagination.page < pagination.totalPages) {
      fetchNotifications(pagination.page + 1, pagination.limit);
    }
  }, [fetchNotifications, pagination.page, pagination.totalPages, pagination.limit]);

  // Initial fetch
  useEffect(() => {
    if (effectiveUserId) {
      fetchNotifications();
      fetchStats();
    }
  }, [effectiveUserId, fetchNotifications, fetchStats]);

  // Auto refresh
  useEffect(() => {
    if (autoRefresh && effectiveUserId) {
      const interval = setInterval(() => {
        fetchNotifications(pagination.page, pagination.limit);
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh, effectiveUserId, fetchNotifications, pagination.page, pagination.limit]);

  return {
    notifications,
    stats,
    loading,
    error,
    pagination,
    unreadCount,
    actions: {
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      refresh,
      loadMore
    }
  };
};

// Hook for unread notification count only (lightweight)
export const useUnreadCount = (userId?: string | number, autoRefresh: boolean = true) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const currentUser = getLoggedinUser();
  const effectiveUserId = userId || currentUser?.user?.id;

  const fetchUnreadCount = useCallback(async () => {
    if (!effectiveUserId) {
      return;
    }

    if (!currentUser?.token) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${config.api.API_URL}/notifications/user/${effectiveUserId}/stats`,
        {
          headers: {
            'Authorization': `Bearer ${currentUser?.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUnreadCount(data.data.overview.unread);
        }
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, currentUser?.token]);

  useEffect(() => {
    if (effectiveUserId) {
      fetchUnreadCount();
    }
  }, [effectiveUserId, fetchUnreadCount]);

  useEffect(() => {
    if (autoRefresh && effectiveUserId) {
      const interval = setInterval(fetchUnreadCount, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [autoRefresh, effectiveUserId, fetchUnreadCount]);

  return { unreadCount, loading, refresh: fetchUnreadCount };
};